import {Tooltip} from "@/components/Common/Tooltip";
import SegmentedToggle from "@/components/Common/forms/SegmentedToggle";
import {useTheme} from "@/hooks/useTheme";
import clsxm from "@/lib/clsxm";
import {capturePostHogEvent, PostHogEvents} from "@/posthog";
import {openTopBarPopoverAtom, ThemeMode} from "@/store/jotaiAtoms";
import {useAtom} from "jotai";
import {Monitor, Moon, Sun} from "lucide-react";
import {type FC} from "react";
import {useTranslation} from "react-i18next";

const OPTIONS: {Icon: typeof Sun; labelKey: "themeDark" | "themeLight" | "themeSystem"; mode: ThemeMode}[] = [
  {Icon: Sun, labelKey: "themeLight", mode: "light"},
  {Icon: Moon, labelKey: "themeDark", mode: "dark"},
  {Icon: Monitor, labelKey: "themeSystem", mode: "system"},
];

const ThemeSelector: FC<{
  variant?: "popover" | "segmented";
}> = ({variant = "popover"}) => {
  const {t} = useTranslation("common");
  const {mode, resolvedTheme, setMode} = useTheme();
  const [openPopover, setOpenPopover] = useAtom(openTopBarPopoverAtom);
  const TriggerIcon = resolvedTheme === "dark" ? Moon : Sun;

  const selectMode = (next: ThemeMode) => {
    setMode(next);
    if (next !== mode) capturePostHogEvent(PostHogEvents.ThemeChanged, {theme: next});
  };

  if (variant === "segmented") {
    return (
      <SegmentedToggle
        className="h-11 w-fit grow-0 rounded-lg max-sm:h-10"
        options={OPTIONS.map(({Icon, labelKey, mode: optionMode}) => ({
          icon: <Icon className="size-3.5 shrink-0" />,
          label: t(labelKey),
          onClick: () => selectMode(optionMode),
        }))}
        selectedIndex={OPTIONS.findIndex(option => option.mode === mode)}
        titleClassName="rounded-md text-xs max-sm:h-8"
        variant="elevated"
      />
    );
  }

  return (
    <div className="flex items-center gap-1.5">
      <span
        data-testid="theme-selector"
        data-tooltip-id="theme-selector-tooltip"
        className="text-primary-dark-solid flex size-5 shrink-0 cursor-pointer items-center justify-center transition-all duration-150 hover:scale-[1.1] hover:opacity-75">
        <TriggerIcon className="size-5 shrink-0" />
      </span>
      <Tooltip
        id="theme-selector-tooltip"
        isOpen={openPopover === "theme"}
        setIsOpen={isOpen => setOpenPopover(prev => (isOpen ? "theme" : prev === "theme" ? null : prev))}
        openEvents={{click: true}}
        closeEvents={{click: true}}
        globalCloseEvents={{clickOutsideAnchor: true, escape: true}}
        clickable
        variant={resolvedTheme === "dark" ? "dark" : "light"}
        place="bottom"
        opacity={1}
        className="z-100 p-0 shadow-md"
        classNameArrow="shadow-md">
        <div className="bg-popover text-popover-foreground flex flex-col divide-y">
          {OPTIONS.map(({Icon, labelKey, mode: optionMode}) => (
            <button
              key={optionMode}
              data-testid={`theme-option-${optionMode}`}
              onClick={() => selectMode(optionMode)}
              className={clsxm(
                "hover:bg-accent flex min-h-12 items-center gap-2 px-6 transition-all duration-150",
                mode === optionMode ? "text-primary-dark-solid" : "text-foreground",
              )}>
              <Icon className="size-4" />
              <span className="font-medium">{t(labelKey)}</span>
            </button>
          ))}
        </div>
      </Tooltip>
    </div>
  );
};

export default ThemeSelector;
